<template>
  <div class="flex flex-col gap-6">
    <div class="flex justify-end">
      <Button variant="outline" :disabled="isLoading" @click="fetchAuthorization">
        <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': isLoading }" />
        {{ $t('chat.admin.refresh') }}
      </Button>
    </div>

    <Alert v-if="errorMessage" variant="destructive">
      <AlertDescription>{{ errorMessage }}</AlertDescription>
    </Alert>

    <section class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div class="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{{ $t('chat.admin.role') }}</TableHead>
              <TableHead>{{ $t('chat.admin.permissions') }}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="role in roleRows" :key="role.name">
              <TableCell class="font-medium">
                {{ role.name }}
              </TableCell>
              <TableCell>
                <div class="flex flex-wrap gap-2">
                  <Badge v-for="permission in role.permissions" :key="`${role.name}-${permission}`" variant="secondary">
                    {{ permission }}
                  </Badge>
                </div>
              </TableCell>
            </TableRow>
            <TableRow v-if="!isLoading && roleRows.length === 0">
              <TableCell colspan="2" class="h-24 text-center text-sm text-muted-foreground">
                {{ $t('chat.admin.noRoles') }}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <aside class="rounded-md border p-4">
        <h2 class="text-sm font-semibold uppercase tracking-normal text-muted-foreground">
          {{ $t('chat.admin.permissions') }}
        </h2>
        <div class="mt-4 flex flex-col gap-2">
          <Badge v-for="permission in permissions" :key="permission" variant="outline" class="w-fit">
            {{ permission }}
          </Badge>
        </div>
      </aside>
    </section>
  </div>
</template>

<script setup>
import axios from 'axios'
import { computed, onMounted, ref } from 'vue'
import { RefreshCw } from 'lucide-vue-next'
import { Alert, AlertDescription } from '../../../../../../resources/js/components/ui/alert/index.js'
import { Badge } from '../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../resources/js/components/ui/button/index.js'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../../resources/js/components/ui/table/index.js'

const isLoading = ref(false)
const errorMessage = ref('')
const authorization = ref({
  permissions: [],
  roles: {},
})

const permissions = computed(() => authorization.value.permissions || [])
const roleRows = computed(() => Object.entries(authorization.value.roles || {}).map(([name, rolePermissions]) => ({
  name,
  permissions: rolePermissions,
})))

const fetchAuthorization = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const response = await axios.get('/api/v1/chat/admin/authorization')
    authorization.value = response.data.data
  } catch (error) {
    errorMessage.value = error?.response?.data?.message || error?.message || 'Unable to load chat administration.'
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchAuthorization()
})
</script>
